import { useCallback, useMemo, useState } from "react"

/**
 * Five semantic presentation states. Closed is policy/explicit-control only;
 * dragging moves among guidance, composer, half, and full.
 */
export type BearingSheetDetent = "closed" | "guidance" | "composer" | "half" | "full"

export interface DetentHeights {
  closed: number
  guidance: number
  composer: number
  half: number
  full: number
}

export const DRAGGABLE_DETENT_ORDER: BearingSheetDetent[] = ["guidance", "composer", "half", "full"]

/** Minimum rail height. The rendered rail may grow so guidance is never clipped. */
export const GUIDANCE_PEEK_PX = 64
/** The middle stop sits at 50% of the full sheet height. */
const HALF_FRACTION = 0.5

export function computeDetentHeights(
  fullHeightPx: number,
  composerHeightPx: number,
  guidanceHeightPx = GUIDANCE_PEEK_PX,
): DetentHeights {
  const measuredGuidance = Math.max(GUIDANCE_PEEK_PX, guidanceHeightPx)
  const guidance = composerHeightPx > 0
    ? Math.min(measuredGuidance, composerHeightPx)
    : measuredGuidance
  return {
    closed: 0,
    guidance,
    composer: composerHeightPx,
    half: fullHeightPx * HALF_FRACTION,
    full: fullHeightPx,
  }
}

/**
 * Apple's momentum-projection formula (WWDC 2018, Designing Fluid Interfaces) —
 * projects where a flick "wants to land" rather than snapping to the raw release
 * point. decelerationRate ≈ 0.998 matches native scroll feel.
 */
export function projectMomentum(velocityPxPerSec: number, decelerationRate = 0.998): number {
  return ((velocityPxPerSec / 1000) * decelerationRate) / (1 - decelerationRate)
}

/**
 * currentHeightPx: the sheet's visible height (px) at release.
 * velocityPxPerSec: the height's rate of change at release — POSITIVE means
 * growing/opening. (Callers translating from motion/react's PanInfo must negate
 * info.velocity.y, which is positive when the pointer moves DOWN the screen.)
 */
export function computeProjectedDetent(
  heights: DetentHeights,
  currentHeightPx: number,
  velocityPxPerSec: number,
): BearingSheetDetent {
  const projectedHeightPx = currentHeightPx + projectMomentum(velocityPxPerSec)
  let nearest: BearingSheetDetent = "composer"
  let nearestDistance = Infinity
  for (const candidate of DRAGGABLE_DETENT_ORDER) {
    const distance = Math.abs(heights[candidate] - projectedHeightPx)
    if (distance < nearestDistance) {
      nearestDistance = distance
      nearest = candidate
    }
  }
  return nearest
}

export interface UseBearingSheetDetentsOptions {
  /** Measured height (px) of the complete screen-guidance rail. */
  guidanceHeightPx: number
  /** Measured height (px) of the sheet's guidance + composer peek. */
  composerHeightPx: number
  /** Measured height (px) of the sheet's own outer box. Defines Full. */
  fullHeightPx: number
}

export interface UseBearingSheetDetentsResult {
  detent: BearingSheetDetent
  setDetent: (detent: BearingSheetDetent) => void
  heights: DetentHeights
}

export function useBearingSheetDetents({
  guidanceHeightPx,
  composerHeightPx,
  fullHeightPx,
}: UseBearingSheetDetentsOptions): UseBearingSheetDetentsResult {
  const [detent, setDetent] = useState<BearingSheetDetent>("composer")
  const heights = useMemo(
    () => computeDetentHeights(fullHeightPx, composerHeightPx, guidanceHeightPx),
    [fullHeightPx, composerHeightPx, guidanceHeightPx],
  )
  const stableSetDetent = useCallback((next: BearingSheetDetent) => setDetent(next), [])
  return { detent, setDetent: stableSetDetent, heights }
}
